import { useCallback, useEffect, useState } from "react";
import {
  fetchPartnerships,
  PartnershipRoleFilter,
  PartnershipStatusFilter,
  PartnershipView,
} from "../api/partnership";

/**
 * Charge les partenariats de l'entreprise connectée.
 *
 * Utilisé à deux endroits : la page Partenaires (liste complète) et le badge de la navbar
 * (`{ status: "pending" }` pour le seul compteur). `reload` permet de rafraîchir après une
 * action sans remonter le composant.
 */
export const usePartnerships = (
  filters?: {
    status?: PartnershipStatusFilter;
    role?: PartnershipRoleFilter;
  },
  /** À false, aucune requête n'est émise (ex. espace admin, non concerné). */
  enabled = true
) => {
  const [partnerships, setPartnerships] = useState<PartnershipView[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const status = filters?.status;
  const role = filters?.role;

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    const result = await fetchPartnerships({ status, role });
    setPartnerships(result.partnerships);
    setError(result.error);
    setLoading(false);
  }, [status, role, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { partnerships, loading, error, reload: load };
};

/**
 * Nombre de demandes en attente auxquelles l'utilisateur peut RÉPONDRE.
 *
 * On compte `can_respond` et non `status === "pending"` : mes propres demandes sont elles
 * aussi « en attente », mais les afficher dans un badge d'action serait trompeur — je n'ai
 * rien à y faire, c'est l'autre entreprise qui doit répondre.
 */
export const usePendingPartnershipCount = (enabled = true) => {
  const { partnerships, reload } = usePartnerships({ status: "pending" }, enabled);
  return {
    count: partnerships.filter((p) => p.can_respond).length,
    reload,
  };
};
